import { UserPreference } from 'components/framework/models';
import { useEffect, useRef } from 'react';
// @ts-ignore
import Shepherd from 'javascripts/shepherd';

interface UseTourOptions {
  key: string;
  getTarget: () => HTMLElement | null;
  buildTour: (tour: Shepherd.Tour) => void;
  enabled: boolean;
  tourOptions?: Record<string, unknown>;
}

/**
 * @example
 * ```typescript
 * useTour({
 *   key: 'feature.tour_seen',
 *   getTarget: () => document.querySelector('.my-button'),
 *   enabled: featureEnabled,
 *   buildTour: (tour) => {
 *     tour.addStep({
 *       title: 'New Feature',
 *       text: 'Click here to get started',
 *       attachTo: { element: '.my-button', on: 'right' },
 *       buttons: [{ text: 'Got it', action: () => tour.complete() }]
 *     });
 *   }
 * });
 * ```
 */
export const useTour = ({ key, enabled, ...options }: UseTourOptions) => {
  const optsRef = useRef(options);

  useEffect(() => {
    if (!enabled) return;

    let tour: Shepherd.Tour | undefined;

    const showTour = async () => {
      const cached = localStorage.getItem(key);
      if (cached) return;

      const tourSeen = await UserPreference.select('value').findBy({
        name: key,
      });
      if (tourSeen?.value) {
        return;
      }

      await UserPreference.setUserPreference(key, 'true');
      localStorage.setItem(key, 'true');

      const waitForElement = () => {
        return new Promise<HTMLElement | void>((resolve, reject) => {
          let checkTimeout: NodeJS.Timeout;
          const rejectTimeout = setTimeout(() => {
            clearTimeout(checkTimeout);
            resolve();
          }, 1000);

          const check = () => {
            const element = optsRef.current.getTarget();
            if (element) {
              clearTimeout(rejectTimeout);
              clearTimeout(checkTimeout);
              resolve(element);
            } else {
              checkTimeout = setTimeout(check, 100);
            }
          };
          check();
        });
      };

      const el = await waitForElement();
      if (!el) {
        console.warn(`useTour with key '${key}' timed out waiting for element`);
        return;
      }

      tour = new Shepherd.Tour({
        defaults: {
          classes: 'shepherd-theme-arrows',
          cancelIcon: {
            enabled: true,
          },
        },
        useModalOverlay: true,
        ...optsRef.current.tourOptions,
      });

      optsRef.current.buildTour(tour);
      tour.start();
    };

    void showTour();

    return () => {
      tour?.complete();
    };
  }, [key, enabled]);
};

export default useTour;
